import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

/** A menu anchored to a trigger — for the account and language menus.
 * `aria-expanded`, click-outside and `Escape` close it. */
export function Dropdown({
  trigger,
  children,
  align = 'end',
  placement = 'bottom',
  stretch = false,
  label,
  className,
}: {
  /** Render prop so the trigger can reflect open state (e.g. a chevron). */
  trigger: (props: { open: boolean }) => ReactNode;
  children: ReactNode;
  align?: 'start' | 'end';
  /** `'top'` opens the panel above the trigger instead of below — for a
   * trigger pinned near the bottom of the viewport (e.g. the sidebar's
   * account row), where opening downward would run off-screen. */
  placement?: 'top' | 'bottom';
  /** The trigger fills its container's width instead of shrink-wrapping —
   * for a trigger styled as a full-width row (e.g. a sidebar item). */
  stretch?: boolean;
  /** `aria-label` on the trigger button when it has no visible text. */
  label?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={cn('relative', stretch ? 'block w-full' : 'inline-block', className)}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={label ?? t('ui.openMenu')}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'rounded-pill focus-visible:ring-accent focus-visible:ring-offset-paper focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
          stretch && 'block w-full',
        )}
      >
        {trigger({ open })}
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          className={cn(
            'border-line bg-paper text-ink absolute z-40 min-w-48 rounded-2xl border p-3 shadow-lg',
            placement === 'top' ? 'bottom-[calc(100%+0.875rem)]' : 'top-[calc(100%+0.875rem)]',
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
